import {
	Box,
	Card,
	IconButton,
	Stack,
	Typography,
	Button,
} from '@mui/material';

import React, { useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import DOMPurify from 'dompurify';
import { groupBy } from 'lodash';

import { infoIcon } from 'common/assets/images';
import { ReactComponent as Comment } from 'common/assets/images/comment.svg';
import AccordionComp from 'common/components/Accordion/AccordionComp';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import { OBLIGATION_RESULT_TYPE } from 'common/constants/AISimulationConstants';

import { IResultCardProps } from '../api/type-def';

import ExertComments from './ExertComments';
import PDFDisplayDrawer from './PDFDisplayDrawer';
import SourcesDrawer from './SourcesDrawer';

import styles from './ResultCard.module.scss';

const ResultCard: React.FC<IResultCardProps> = ({
	obligationOutput,
	saveComment,
}) => {
	const { analysis, sources, obligation } = obligationOutput;

	const [fileName, setFileName] = useState<string>('');
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [isSourceOpen, setIsSourceOpen] = useState<boolean>(false);
	const [isPdfOpen, setIsPdfOpen] = useState<boolean>(false);
	const [isCommentOpen, setIsCommentOpen] = useState<boolean>(false);
	const [selectedCommentId, setSelectedCommentId] = useState<string>('');
	const [selectedAccordians, setSelectedAccordians] = useState<string[]>([]);
	const { commentIconColor } = styles;

	const getFileInfoFromSource = (source: string | undefined) => {
		if (!source) return '';
		const [file, page] = source.split('#');
		const name = file.replace('.pdf', '');
		const pageNum = Number(page.replace('page=', ''));
		return { name, pageNum };
	};

	const handleClickPage = (source: string | undefined) => {
		if (!source) return;
		const { name, pageNum } = getFileInfoFromSource(source) as {
			name: string;
			pageNum: number;
		};
		setFileName(name);
		setPageNumber(pageNum);
		setIsPdfOpen(true);
	};

	const handleCommentBtn = (source: string | undefined) => {
		if (!source) return;
		const { name, pageNum } = getFileInfoFromSource(source) as {
			name: string;
			pageNum: number;
		};
		setFileName(name);
		setPageNumber(pageNum);
		setIsCommentOpen(true);
	};

	const handleAccordianChange = (event: React.SyntheticEvent, val: any) => {
		event.stopPropagation();
		if (selectedAccordians.includes(val.displayValue)) {
			setSelectedAccordians((res) =>
				res.filter((accordian) => accordian !== val.displayValue),
			);
		} else {
			setSelectedAccordians([...selectedAccordians, val.displayValue]);
		}
	};

	const groupedAnalysis = groupBy(analysis, 'result');
	return (
		<Card className={styles.card}>
			<Box sx={{ position: 'relative' }}>
				<Stack direction="row" justifyContent="space-between">
					<Typography className={styles['obligation-text']}>
						{obligation}
					</Typography>
					<IconButton
						onClick={() => setIsSourceOpen(true)}
						disableRipple
						className={styles['source-icon']}
						data-testid="source-btn"
					>
						<img
							src={infoIcon}
							alt="info-link"
							className={styles['info-source-icon']}
						/>
					</IconButton>
				</Stack>
			</Box>

			{OBLIGATION_RESULT_TYPE.map((ele) => {
				const matchedResult = groupedAnalysis[ele.result] || [];
				const count = matchedResult ? matchedResult.length : 0;
				return (
					<AccordionComp
						key={uuidv4()}
						className={styles[ele.class]}
						detailsStyle={styles['accordian-detail']}
						dataTestId="policy-accordian"
						isLeftAccordian
						expanded={selectedAccordians.includes(ele.displayValue)}
						onChange={(event) => handleAccordianChange(event, ele)}
						summary={
							<Typography data-testid="accordian-heading">
								{ele.displayValue} ({count})
							</Typography>
						}
						details={
							matchedResult.length > 0 ? (
								<>
									<Typography className={styles['analysis-header']}>
										Excerpts from the policies and procedures:
									</Typography>
									{matchedResult.map((item) => (
										<div key={uuidv4()} className={styles['source-style']}>
											<ul>
												<li>
													{item.raw && (
														<div
															dangerouslySetInnerHTML={{
																__html: DOMPurify.sanitize(item.raw),
															}}
															className={styles['analysis-property']}
														/>
													)}
													<div className={styles['analysis-property']}>
														<strong>Citation:</strong>
														<Button
															className={styles['page-link']}
															onClick={() => handleClickPage(item.source)}
														>
															{item.source}
														</Button>
														(Relevance Level: {item.relevance})
													</div>
													<div className={styles['analysis-property']}>
														<strong>Analysis:</strong> {item.analysis}
													</div>
													<div className={styles['analysis-property']}>
														<strong>Result:</strong> {item.result}
													</div>
													<div>
														<strong>Remediation:</strong>{' '}
														{item.remediation || 'N/A'}
													</div>
												</li>
											</ul>
											<div className={styles['comment-icon']}>
												<IconButton
													disableRipple
													data-testid="comment-btn"
													onClick={() => {
														handleCommentBtn(item.source);
														if (item.id) {
															setSelectedCommentId(item.id);
														}
													}}
												>
													<Comment fill={commentIconColor} />
												</IconButton>
											</div>
										</div>
									))}
								</>
							) : (
								<Typography className={styles['analysis-header']}>
									No corresponding extract of policies and procedures
								</Typography>
							)
						}
					/>
				);
			})}

			<SideDrawer
				className={styles['sources-drawer']}
				drawerState={isCommentOpen}
			>
				<ExertComments
					handleClickClose={() => setIsCommentOpen(false)}
					pageNumber={pageNumber}
					Id={selectedCommentId}
					saveParagraphComment={saveComment}
				/>
			</SideDrawer>
			<SideDrawer
				className={styles['sources-drawer']}
				drawerState={isSourceOpen}
			>
				<SourcesDrawer
					sources={sources}
					handleClickClose={() => setIsSourceOpen(false)}
				/>
			</SideDrawer>
			<SideDrawer className={styles['sources-drawer']} drawerState={isPdfOpen}>
				<PDFDisplayDrawer
					fileName={fileName}
					pageNumber={pageNumber}
					handleClickClose={() => setIsPdfOpen(false)}
				/>
			</SideDrawer>
		</Card>
	);
};

export default ResultCard;
